import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import ReactFlow, {
  Node,
  Edge,
  Background,
  Controls,
  MiniMap,
  useNodesState,
  useEdgesState,
  MarkerType,
  Position,
  ReactFlowInstance,
  NodeProps,
  Handle
} from 'reactflow';
import 'reactflow/dist/style.css';
import { Box, Typography, Paper, CircularProgress, TextField, InputAdornment, Button } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { loadEmployees, Employee } from '../utils/employeeData';

type EmployeeNodeData = {
  label: string;
  name: string;
  position: string;
  employeeCode: string;

  // injected at render-time (not persisted)
  hasChildren?: boolean;
  subtreeDirection?: 'LR' | 'TB';
  onToggleSubtree?: (id: string) => void;
};

// Name it something that won't confuse TS inference
type OrgNode = Node<EmployeeNodeData>;
type OrgEdge = Edge;

const NODE_WIDTH = 220;
const NODE_HEIGHT = 80;
const STORAGE_KEY = 'orgchart_state_v1';

// spacing used for subtree toggle layout
const H_GAP = 40;
const V_GAP = 40;

/** ------------------ Toggle helpers ------------------ */
const buildChildrenLookup = (edges: OrgEdge[]) => {
  const map = new Map<string, string[]>();
  edges.forEach((e) => {
    if (!map.has(e.source)) map.set(e.source, []);
    map.get(e.source)!.push(e.target);
  });
  return map;
};

/**
 * Layout only the subtree under rootId.
 * - 'TB': children stacked vertically under each parent
 * - 'LR': children spread horizontally under each parent
 * Keeps root position unchanged; updates descendants only.
 */
const layoutSubtree = (
  rootId: string,
  direction: 'TB' | 'LR',
  nodes: OrgNode[],
  edges: OrgEdge[]
): OrgNode[] => {
  const childrenMap = buildChildrenLookup(edges);
  const nodeById = new Map(nodes.map((n) => [n.id, n]));
  const root = nodeById.get(rootId);
  if (!root) return nodes;

  const updatedPos = new Map<string, { x: number; y: number }>();

  const layoutRecursive = (id: string, x: number, y: number) => {
    updatedPos.set(id, { x, y });

    const kids = childrenMap.get(id) ?? [];
    if (kids.length === 0) return;

    if (direction === 'TB') {
      // stack children BELOW the parent, but nudged slightly RIGHT
      // (creates room for a left-side "spine" coming off the parent)
      const X_OFFSET = 60; // tweak 40–90 to taste

      const childX = x + X_OFFSET;
      let childY = y + NODE_HEIGHT + V_GAP;

      kids.forEach((kidId) => {
        layoutRecursive(kidId, childX, childY);
        childY += NODE_HEIGHT + V_GAP;
      });
    } else {
      // spread children horizontally under parent (same y)
      const totalWidth = kids.length * NODE_WIDTH + (kids.length - 1) * H_GAP;
      let childX = x - totalWidth / 2 + NODE_WIDTH / 2;
      const childY = y + NODE_HEIGHT + V_GAP;

      kids.forEach((kidId) => {
        layoutRecursive(kidId, childX, childY);
        childX += NODE_WIDTH + H_GAP;
      });
    }
  };

  layoutRecursive(rootId, root.position.x, root.position.y);

  return nodes.map((n) => {
    const pos = updatedPos.get(n.id);
    return pos ? { ...n, position: pos } : n;
  });
};

/** ------------------ Custom node (adds toggle button) ------------------ */
function EmployeeNodeComponent({ data }: NodeProps<EmployeeNodeData>) {
  const canToggle = Boolean(data.hasChildren);
  const dir = data.subtreeDirection ?? 'LR';

  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* TOP / BOTTOM handles (horizontal layout) */}
      <Handle id="t" type="target" position={Position.Top} style={{ opacity: 0 }} />
      <Handle id="b" type="source" position={Position.Bottom} style={{ opacity: 0 }} />

      {/* LEFT / RIGHT handles (vertical stack layout) */}
      <Handle id="lt" type="target" position={Position.Left} style={{ opacity: 0 }} />
      <Handle id="ls" type="source" position={Position.Left} style={{ opacity: 0 }} />
      <Handle id="r" type="source" position={Position.Right} style={{ opacity: 0 }} />

      {canToggle && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            data.onToggleSubtree?.(data.employeeCode);
          }}
          title={dir === 'LR' ? 'Switch children to vertical' : 'Switch children to horizontal'}
          style={{
            position: 'absolute',
            top: 4,
            right: 4,
            width: 26,
            height: 26,
            borderRadius: 6,
            border: '1px solid #1976d2',
            background: '#fff',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: '24px',
            padding: 0,
            zIndex: 10,
          }}
        >
          {dir === 'LR' ? '↕' : '↔'}
        </button>
      )}

      <div style={{ whiteSpace: 'pre-wrap' }}>{data.label}</div>
    </div>
  );
}

const nodeTypes = { employee: EmployeeNodeComponent };
const edgeTypes = {};

const OrgChart: React.FC = () => {
  // IMPORTANT: useNodesState generic is the *data* type, not the node type
  const [nodes, setNodes, onNodesChange] = useNodesState<EmployeeNodeData>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<OrgEdge>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [reactFlowInstance, setReactFlowInstance] = useState<ReactFlowInstance | null>(null);

  const savedViewportRef = useRef<{ x: number; y: number; zoom: number } | null>(null);
  const saveTimerRef = useRef<number | null>(null);

  const [highlightedIds, setHighlightedIds] = useState<Set<string>>(new Set());
  const lastPannedToRef = useRef<string>('');

  // Toggle state: nodeId -> 'LR' | 'TB'
  const [subtreeDirection, setSubtreeDirection] = useState<Record<string, 'LR' | 'TB'>>({});

  const buildOrgChart = useCallback(
    (employees: Employee[]) => {
      const employeeMap = new Map<string, Employee>();
      const childrenMap = new Map<string, Employee[]>();

      employees.forEach((emp) => {
        employeeMap.set(emp.employeeCode, emp);

        if (
          emp.supervisorCode &&
          emp.supervisorCode.trim() !== '' &&
          emp.supervisorCode !== emp.employeeCode
        ) {
          if (!childrenMap.has(emp.supervisorCode)) {
            childrenMap.set(emp.supervisorCode, []);
          }
          childrenMap.get(emp.supervisorCode)!.push(emp);
        }
      });

      const rootEmployees = employees.filter(
        (emp) =>
          !emp.supervisorCode ||
          emp.supervisorCode.trim() === '' ||
          !employeeMap.has(emp.supervisorCode) ||
          emp.supervisorCode === emp.employeeCode
      );

      const newNodes: OrgNode[] = [];
      const newEdges: OrgEdge[] = [];

      const nodeSpacing = 240;
      const levelHeight = 120;
      const nodeWidth = 220;

      let currentXOffset = 0;

      const addNodeWithChildren = (employee: Employee, level: number, xPosition: number): number => {
        const y = level * levelHeight;
        const children = childrenMap.get(employee.employeeCode) || [];

        let subtreeWidth = 0;
        const childCenters: number[] = [];

        if (children.length > 0) {
          let childXStart = xPosition;
          children.forEach((child) => {
            const childWidth = addNodeWithChildren(child, level + 1, childXStart);
            childCenters.push(childXStart + childWidth / 2);
            childXStart += childWidth;
            subtreeWidth += childWidth;
          });
        } else {
          subtreeWidth = nodeSpacing;
        }

        const x =
          children.length > 0
            ? (childCenters[0] + childCenters[childCenters.length - 1]) / 2
            : xPosition + subtreeWidth / 2;

        newNodes.push({
          id: employee.employeeCode,
          type: 'default',
          position: { x, y },
          data: {
            label: `${employee.employeeName}\n${employee.position || 'Position N/A'}`,
            name: employee.employeeName,
            position: employee.position || 'Position N/A',
            employeeCode: employee.employeeCode,
          },
          style: {
            background: '#fff',
            border: '2px solid #1976d2',
            borderRadius: '8px',
            padding: '8px',
            width: nodeWidth,
            fontSize: '11px',
            fontWeight: 'bold',
            lineHeight: '1.3',
          },
          sourcePosition: Position.Bottom,
          targetPosition: Position.Top,
        });

        if (employee.supervisorCode && employeeMap.has(employee.supervisorCode)) {
          newEdges.push({
            id: `${employee.supervisorCode}-${employee.employeeCode}`,
            source: employee.supervisorCode,
            target: employee.employeeCode,
            type: 'smoothstep',
            animated: false,
            style: { stroke: '#1976d2', strokeWidth: 2 },
            markerEnd: { type: MarkerType.ArrowClosed, color: '#1976d2' },
          });
        }

        return subtreeWidth;
      };

      rootEmployees.forEach((root) => {
        const hierarchyWidth = addNodeWithChildren(root, 0, currentXOffset);
        currentXOffset += hierarchyWidth + nodeSpacing;
      });

      setNodes(newNodes);
      setEdges(newEdges);

      // optional: reset subtree directions on a fresh build
      setSubtreeDirection({});
    },
    [setNodes, setEdges]
  );

  /** Toggle handler */
  const handleToggleSubtree = useCallback(
    (nodeId: string) => {
      const nextDir: 'LR' | 'TB' = (subtreeDirection[nodeId] ?? 'LR') === 'LR' ? 'TB' : 'LR';

      setSubtreeDirection((prev) => ({ ...prev, [nodeId]: nextDir }));

      setNodes((prev) => {
        const prevNodes = prev as OrgNode[];
        return layoutSubtree(nodeId, nextDir, prevNodes, edges);
      });
    },
    [edges, setNodes, subtreeDirection]
  );

  const handleResetLayout = useCallback(async () => {
    try {
      // 1) Clear saved layout
      localStorage.removeItem(STORAGE_KEY);

      setSearchTerm('');
      setHighlightedIds(new Set());
      lastPannedToRef.current = '';

      savedViewportRef.current = null;
      setSubtreeDirection({}); // <-- toggle-related reset

      // 2) Rebuild from source data
      setLoading(true);
      const employees = await loadEmployees();
      buildOrgChart(employees);

      // 3) Re-fit view after nodes render
      requestAnimationFrame(() => {
        reactFlowInstance?.fitView({ padding: 0.2, duration: 400 });
      });
    } catch (e) {
      console.error('Failed to reset org chart layout:', e);
    } finally {
      setLoading(false);
    }
  }, [buildOrgChart, reactFlowInstance]);

  useEffect(() => {
    const restoreOrBuild = async () => {
      try {
        // 1) Try restore from localStorage first
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
          const saved = JSON.parse(raw);

          if (saved?.nodes?.length) setNodes(saved.nodes);
          if (saved?.edges?.length) setEdges(saved.edges);

          if (saved?.subtreeDirection) setSubtreeDirection(saved.subtreeDirection); // <-- toggle restore

          if (saved?.viewport) {
            savedViewportRef.current = saved.viewport;
          }

          // If we restored nodes, we can skip rebuilding from employees
          if (saved?.nodes?.length) {
            setLoading(false);
            return;
          }
        }

        // 2) Otherwise build from source data
        const employees = await loadEmployees();
        buildOrgChart(employees);
      } catch (error) {
        console.error('Error restoring/building org chart:', error);
      } finally {
        setLoading(false);
      }
    };

    restoreOrBuild();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildOrgChart, setNodes, setEdges]);

  // Search: update highlighted set + pan to first match
  useEffect(() => {
    if (!reactFlowInstance) return;

    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      setHighlightedIds(new Set());
      lastPannedToRef.current = '';
      return;
    }

    // nodes here are typed as Node<EmployeeNodeData>
    const matches = nodes.filter((n) => (n.data?.name ?? '').toLowerCase().includes(term));
    setHighlightedIds(new Set(matches.map((m) => m.id)));

    if (matches.length > 0 && lastPannedToRef.current !== matches[0].id) {
      lastPannedToRef.current = matches[0].id;

      requestAnimationFrame(() => {
        const rfNode =
          (reactFlowInstance as any).getNode?.(matches[0].id) ||
          reactFlowInstance.getNodes().find((n) => n.id === matches[0].id);

        if (!rfNode) return;

        const pos = (rfNode as any).positionAbsolute ?? rfNode.position;

        reactFlowInstance.setCenter(pos.x + NODE_WIDTH / 2, pos.y + NODE_HEIGHT / 2, {
          zoom: 1.5,
          duration: 800,
        });
      });
    }
  }, [searchTerm, nodes, reactFlowInstance]);

  useEffect(() => {
    if (loading) return;
    if (!reactFlowInstance) return;

    // Debounce saves so we don't write on every tiny change
    if (saveTimerRef.current) {
      window.clearTimeout(saveTimerRef.current);
    }

    saveTimerRef.current = window.setTimeout(() => {
      try {
        const payload = {
          nodes,
          edges,
          subtreeDirection, // <-- toggle persist
          viewport: reactFlowInstance.getViewport(),
          savedAt: new Date().toISOString(),
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      } catch (e) {
        console.warn('Failed to save org chart state:', e);
      }
    }, 250);

    return () => {
      if (saveTimerRef.current) {
        window.clearTimeout(saveTimerRef.current);
        saveTimerRef.current = null;
      }
    };
  }, [nodes, edges, subtreeDirection, reactFlowInstance, loading]);

  const matchCount = useMemo(() => {
    if (!searchTerm.trim()) return 0;
    const term = searchTerm.toLowerCase();
    return nodes.filter((node) => (node.data?.name ?? '').toLowerCase().includes(term)).length;
  }, [searchTerm, nodes]);

  const styledNodes = useMemo(() => {
    const hasSearch = Boolean(searchTerm.trim());
    const childrenLookup = buildChildrenLookup(edges);

    return nodes.map((node) => {
      const hasChildren = (childrenLookup.get(node.id) ?? []).length > 0;

      const baseStyled = {
        ...node,
        type: 'employee', // <-- render custom node
        data: {
          ...node.data,
          hasChildren,
          subtreeDirection: subtreeDirection[node.id] ?? 'LR',
          onToggleSubtree: handleToggleSubtree,
        },
      } as OrgNode;

      if (!hasSearch) {
        return {
          ...baseStyled,
          style: {
            ...baseStyled.style,
            background: '#fff',
            border: '2px solid #1976d2',
            boxShadow: 'none',
          },
        };
      }

      const isMatch = highlightedIds.has(node.id);
      return {
        ...baseStyled,
        style: {
          ...baseStyled.style,
          background: isMatch ? '#fff3cd' : '#f5f5f5',
          border: isMatch ? '3px solid #ff9800' : '1px solid #ccc',
          boxShadow: isMatch ? '0 0 10px rgba(255, 152, 0, 0.5)' : 'none',
        },
      };
    });
  }, [nodes, edges, highlightedIds, searchTerm, subtreeDirection, handleToggleSubtree]);

  const displayEdges = useMemo(() => {
    return edges.map((e) => {
      const dir = subtreeDirection[e.source] ?? 'LR';
      const isVerticalStack = dir === 'TB';

      return {
        ...e,
        // TB: come OFF THE LEFT of the parent, and go INTO the LEFT of the child
        sourceHandle: isVerticalStack ? 'ls' : 'b',
        targetHandle: isVerticalStack ? 'lt' : 't',
      };
    });
  }, [edges, subtreeDirection]);


  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 100px)' }}>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h4" component="h1" gutterBottom>
              Organization Chart
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Visualizing employee hierarchy based on Employee_Code and Supervisor_Primary_Code relationships.
              Use mouse wheel to zoom, drag to pan. Changes are automatically saved, but can be reset with the "Reset layout" button.
            </Typography>
          </Box>

          <Box sx={{ width: 300, ml: 2 }}>
            <Button variant="outlined" size="small" fullWidth sx={{ mt: 1, mb: 1 }} onClick={handleResetLayout}>
              Reset layout
            </Button>

            <TextField
              fullWidth
              size="small"
              placeholder="Search employees..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
            {searchTerm && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {matchCount} match{matchCount !== 1 ? 'es' : ''} found
              </Typography>
            )}
          </Box>
        </Box>
      </Paper>

      <Box sx={{ height: 'calc(100% - 120px)', border: '1px solid #ddd', borderRadius: 1 }}>
        <ReactFlow
          nodes={styledNodes}
          edges={displayEdges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onInit={(instance) => {
            setReactFlowInstance(instance);

            // If we have a saved viewport, apply it. Otherwise fit view once.
            requestAnimationFrame(() => {
              if (savedViewportRef.current) {
                instance.setViewport(savedViewportRef.current, { duration: 0 });
              } else {
                instance.fitView({ padding: 0.2 });
              }
            });
          }}
          attributionPosition="bottom-left"
        >
          <Background />
          <Controls />
          <MiniMap nodeColor={() => '#1976d2'} maskColor="rgba(0, 0, 0, 0.1)" />
        </ReactFlow>
      </Box>
    </Box>
  );
};

export default OrgChart;
